import {styled} from '@volkswagen-onehub/components-core';
import type {
  LayerContentElementV2,
  LayerV2,
} from '@volkswagen-onehub/layer-manager';
import * as React from 'react';
import ReactDOM from 'react-dom';

export interface LayerContentProps {
  readonly layer: LayerV2<unknown, unknown>;
}

function isReact(renderable: LayerContentElementV2): renderable is JSX.Element {
  return typeof renderable !== 'function';
}

const StyledLayerWrapper = styled.div`
  &:focus {
    outline: none;
  }
`;
StyledLayerWrapper.displayName = 'StyledLayerWrapper';

export class LayerContent extends React.PureComponent<LayerContentProps> {
  private readonly ref = React.createRef<HTMLDivElement>();

  private reuse: number = 0;
  private renderable?: LayerContentElementV2;

  public constructor(props: LayerContentProps) {
    super(props);

    this.preRenderContent(props);
  }

  public componentDidMount(): void {
    this.updateDom();
  }

  public UNSAFE_componentWillReceiveProps(): void {
    // when this component is reused and a different layer is rendered, the DOM
    // isn't cleared. For React layer contents this is not a problem. If the
    // previous layer content was not rendered with React, it stays in the DOM.
    // Thus we need to change the key of the div, to force a new one to be created
    // and the DOM to be "cleared"
    this.reuse++;
  }

  public UNSAFE_componentWillUpdate(nextProps: LayerContentProps): void {
    this.preRenderContent(nextProps);
  }

  public componentDidUpdate(): void {
    this.updateDom();
  }

  public render(): JSX.Element {
    return (
      // tabIndex={-1} to allow mixed mouse & keyboard users get the focus inside a layer by clicking into it
      <StyledLayerWrapper key={this.reuse} ref={this.ref} tabIndex={-1}>
        {this.renderable && isReact(this.renderable) ? this.renderable : null}
      </StyledLayerWrapper>
    );
  }

  private preRenderContent(props: LayerContentProps): void {
    this.renderable = props.layer.render();
  }

  private updateDom(): void {
    const ref = ReactDOM.findDOMNode(this.ref.current);
    if (
      this.renderable &&
      !isReact(this.renderable) &&
      ref instanceof HTMLElement
    ) {
      this.renderable(ref);
    }
  }
}
